import { Injectable } from "@angular/core";
import { collection, doc, Firestore, getDocs, limit, orderBy, query, setDoc } from "@angular/fire/firestore";
import { WellnessAggregate } from "../models/wellness.agg";
import { UserService } from "./user.service";
import { SleepUserSegment } from "../models/sleep.segment";

@Injectable({
    providedIn: 'root'
})
export class AggregatesService {
    constructor(
        private fs: Firestore,
        private userService: UserService,
    ) { }

    async fetchLast30WellnessDays() {
        const dailyColl = collection(
            this.fs,
            "v0",
            "data",
            "user",
            this.userService.user.id,
            "wellness",
            "well-agg",
            "well-agg-daily"
        );

        const q = query(dailyColl, orderBy('day', "desc"), limit(30));
        const snap = await getDocs(q);

        const rows: WellnessAggregate[] = [];
        snap.forEach((doc) => {
            const data = doc.data() as Omit<WellnessAggregate, "date">;
            rows.push({ ...data });
        });

        return rows;
    }

    async fetchLast30WellnessDaysByWeek() {
        const rows = await this.fetchLast30WellnessDays();

        const grouped: Record<string, WellnessAggregate[]> = {};
        for (const r of rows) {
            const d = new Date(r.day);
            const firstDayOfYear = new Date(d.getFullYear(), 0, 1);
            const pastDays = Math.floor((d.getTime() - firstDayOfYear.getTime()) / 86400000);
            const week = `${d.getFullYear()}-W${Math.ceil((pastDays + firstDayOfYear.getDay() + 1) / 7)}`;

            if (!grouped[week]) {
                grouped[week] = [];
            }
            grouped[week].push(r);
        }

        const weeks = Object.entries(grouped).map(([week, entries]) => {
            let deep = 0, light = 0, nap = 0, active = 0, steps = 0;

            for (const e of entries) {
                deep += e.sleep?.deepMinutes ?? 0;
                light += e.sleep?.lightMinutes ?? 0;
                nap += e.sleep?.napMinutes ?? 0;
                active += e.activity?.activeMinutes ?? 0;
                steps += e.steps?.totalSteps ?? 0;
            }

            return {
                week,
                deepMinutes: deep,
                lightMinutes: light,
                napMinutes: nap,
                activeMinutes: active,
                totalSteps: steps,
            };
        });

        return weeks.sort((a, b) => a.week.localeCompare(b.week));
    }

    async fetchWellnessByRange(
        type: 'sleep' | 'activity' | 'steps',
        from?: Date,
        to?: Date
    ) {
        const dailyColl = collection(
            this.fs,
            "v0",
            "data",
            "user",
            this.userService.user.id,
            "wellness",
            "well-agg",
            "well-agg-daily"
        );

        const q = query(dailyColl, orderBy('day', "desc"), limit(90));
        const snap = await getDocs(q);

        const rows: WellnessAggregate[] = [];
        snap.forEach((doc) => {
            const data = doc.data() as Omit<WellnessAggregate, "date">;
            rows.push({ ...data });
        });

        let nonEmpty = rows.filter(r => {
            switch (type) {
                case 'sleep':
                    return (r.sleep?.deepMinutes ?? 0) > 0 ||
                        (r.sleep?.lightMinutes ?? 0) > 0 ||
                        (r.sleep?.napMinutes ?? 0) > 0;
                case 'activity':
                    return (r.activity?.activeMinutes ?? 0) > 0;
                case 'steps':
                    return (r.steps?.totalSteps ?? 0) > 0;
            }
        });

        if (!from || !to) {
            if (nonEmpty.length === 0) return [];
            const lastDay = new Date(nonEmpty[0].day);
            to = lastDay;
            from = new Date(lastDay);
            from.setDate(from.getDate() - 6);
        }

        nonEmpty = nonEmpty.filter(r => {
            const d = new Date(r.day);
            return d >= from! && d <= to!;
        });

        return nonEmpty;
    }

    async saveDailyAggregateSleep(segment: SleepUserSegment): Promise<void> {
        const dayString = new Date(segment.start).toISOString().split("T")[0];
        if (!dayString) return;
        const path = `v0/data/user/${this.userService.user.id}/wellness/well-agg/well-agg-daily/${dayString}`;
        const docRef = doc(this.fs, path);

        // izračun trajanja
        const totalMinutes = Math.floor((segment.end.getTime() - segment.start.getTime()) / 60000);
        const lightMinutes = totalMinutes;
        const deepMinutes = Math.max(0, totalMinutes - lightMinutes);

        const update = {
            start: segment.start.getTime(),
            end: segment.end.getTime(),
            totalMinutes,
            lightMinutes,
            deepMinutes,
            napMinutes: 0,
            progress: 0,
        };

        await setDoc(
            docRef,
            {
                sleep: update,
                day: dayString,
            },
            { merge: true }
        );
    }

    async fetchLast7DaysForType(type: 'sleep' | 'activity' | 'steps') {
        const dailyColl = collection(
            this.fs,
            "v0",
            "data",
            "user",
            this.userService.user.id,
            "wellness",
            "well-agg",
            "well-agg-daily"
        );

        const q = query(dailyColl, orderBy('day', "desc"));
        const snap = await getDocs(q);

        const rows: WellnessAggregate[] = [];
        snap.forEach((doc) => {
            const data = doc.data() as Omit<WellnessAggregate, "date">;
            rows.push({ ...data });
        });

        let nonEmpty: WellnessAggregate[] = [];
        switch (type) {
            case 'sleep':
                nonEmpty = rows.filter(r =>
                    (r.sleep?.deepMinutes ?? 0) > 0 ||
                    (r.sleep?.lightMinutes ?? 0) > 0 ||
                    (r.sleep?.napMinutes ?? 0) > 0
                );
                break;
            case 'activity':
                nonEmpty = rows.filter(r => (r.activity?.activeMinutes ?? 0) > 0);
                break;
            case 'steps':
                nonEmpty = rows.filter(r => (r.steps?.totalSteps ?? 0) > 0);
                break;
        }

        if (nonEmpty.length === 0) {
            return [];
        }


        const last = nonEmpty[0];
        const lastDate = new Date(last.day);

        const from = new Date(lastDate);
        from.setDate(from.getDate() - 6);

        const result = rows
            .filter(r => {
                const d = new Date(r.day);
                return d >= from && d <= lastDate;
            })
            .sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());

        return result;
    }

}
